import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { getPendingCount, syncPendingReadings } from '../utils/offlineQueue';
import api from '../utils/api';
import { useTranslation, LanguageSwitcher } from '../i18n';
import {
  Menu, X, Camera, List, AlertTriangle, FileText, TrendingUp,
  Users, LogOut, BarChart3, MapPin, Settings, RefreshCw, Gauge, Map, AlertCircle,
  Key, Eye, EyeOff, CheckCircle, Receipt, DollarSign, Download, Briefcase,
  BookOpen, FileInput, Calculator, ChevronDown, ChevronRight, Landmark, FileBarChart, Megaphone,
} from 'lucide-react';

export default function Navigation() {
  const { user, logout } = useAuth();
  const { orgName } = useOrg();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [openReports, setOpenReports] = useState(0);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);

  useEffect(() => {
    const checkPending = async () => {
      try { setPendingCount(await getPendingCount()); } catch {}
    };
    checkPending();
    const interval = setInterval(checkPending, 5000);

    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => { clearInterval(interval); window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);

  // Fetch open reports count for kontrolor/admin badge
  useEffect(() => {
    if (!user || !['kontrolor', 'admin', 'vodoinstalater'].includes(user.role)) return;
    const fetchCount = () => api.get('/reports/count').then(r => setOpenReports(r.data.open || 0)).catch(() => {});
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleSync = async () => {
    if (syncing || !online) return;
    setSyncing(true);
    try {
      await syncPendingReadings((s, t) => setPendingCount(t - s));
      setPendingCount(await getPendingCount());
    } catch {}
    finally { setSyncing(false); }
  };

  const handleLogout = () => { logout(); navigate('/admin/login'); setMenuOpen(false); };

  if (!user) return null;

  // 9.6: Prefix matching for active nav state — /invoices/123 highlights "Invoices"
  // Home path "/" uses exact match to avoid highlighting it for every route
  const isActive = (path) => path === '/admin'
    ? location.pathname === '/admin'
    : location.pathname === path || location.pathname.startsWith(path + '/');

  const navItems = {
    inkasant: [
      { path: '/admin', icon: <Camera size={20} />, label: t('nav.capture') },
      { path: '/admin/my-readings', icon: <List size={20} />, label: t('nav.myReadings') },
      { path: '/admin/unread', icon: <AlertCircle size={20} />, label: t('nav.unread') },
      { path: '/admin/report', icon: <AlertTriangle size={20} />, label: t('nav.reportFailure') },
    ],
    vodoinstalater: [
      { path: '/admin', icon: <AlertTriangle size={20} />, label: t('nav.reports'), badge: openReports },
    ],
    kontrolor: [
      { path: '/admin', icon: <BarChart3 size={20} />, label: t('nav.dashboard') },
      { path: '/admin/readings', icon: <List size={20} />, label: t('nav.readings') },
      { path: '/admin/unread', icon: <AlertCircle size={20} />, label: t('nav.unread') },
      { path: '/admin/reports', icon: <AlertTriangle size={20} />, label: t('nav.reports'), badge: openReports },
      { path: '/admin/map', icon: <Map size={20} />, label: t('nav.map') },
      { path: '/admin/reports-dashboard', icon: <TrendingUp size={20} />, label: t('nav.reportsDashboard') },
      { path: '/admin/export', icon: <FileText size={20} />, label: t('nav.export') },
      { path: '/admin/announcements', icon: <Megaphone size={20} />, label: t('nav.announcements') },
    ],
    obracun: [
      { path: '/admin', icon: <BarChart3 size={20} />, label: t('nav.billing') },
      { path: '/admin/invoices', icon: <Receipt size={20} />, label: t('nav.invoices') },
      { path: '/admin/payments', icon: <DollarSign size={20} />, label: t('nav.payments') },
      { path: '/admin/kartica', icon: <FileText size={20} />, label: t('nav.kartica') },
      { path: '/admin/izvjestaji', icon: <TrendingUp size={20} />, label: t('nav.izvjestaji') },
      { path: '/admin/partners', icon: <Briefcase size={20} />, label: t('nav.partners') },
      { path: '/admin/billing-export', icon: <Download size={20} />, label: t('nav.billingExport') },
      { path: '/admin/ledger', icon: <BookOpen size={20} />, label: t('nav.ledger') },
      { path: '/admin/purchase-invoices', icon: <FileInput size={20} />, label: t('nav.purchaseInvoices') },
      { path: '/admin/vat-return', icon: <Calculator size={20} />, label: t('nav.vatReturn') },
      { path: '/admin/financial-reports', icon: <FileBarChart size={20} />, label: t('nav.financialReports') },
    ],
    kasa: [
      { path: '/admin', icon: <DollarSign size={20} />, label: t('nav.kasa') },
      { path: '/admin/kartica', icon: <FileText size={20} />, label: t('nav.kartica') },
    ],
    admin: [
      { path: '/admin', icon: <BarChart3 size={20} />, label: t('nav.dashboard') },
      { path: '/admin/readings', icon: <List size={20} />, label: t('nav.readings') },
      { path: '/admin/unread', icon: <AlertCircle size={20} />, label: t('nav.unread') },
      { path: '/admin/reports', icon: <AlertTriangle size={20} />, label: t('nav.reports'), badge: openReports },
      { path: '/admin/map', icon: <Map size={20} />, label: t('nav.map') },
      { path: '/admin/reports-dashboard', icon: <TrendingUp size={20} />, label: t('nav.reportsDashboard') },
      { path: '/admin/export', icon: <FileText size={20} />, label: t('nav.export') },
      { label: t('nav.finansijsko'), icon: <Landmark size={20} />, group: true, children: [
        { path: '/admin/invoices', icon: <Receipt size={18} />, label: t('nav.invoices') },
        { path: '/admin/payments', icon: <DollarSign size={18} />, label: t('nav.payments') },
        { path: '/admin/kasa', icon: <DollarSign size={18} />, label: t('nav.kasa') },
        { path: '/admin/kartica', icon: <FileText size={18} />, label: t('nav.kartica') },
        { path: '/admin/izvjestaji', icon: <TrendingUp size={18} />, label: t('nav.izvjestaji') },
        { path: '/admin/partners', icon: <Briefcase size={18} />, label: t('nav.partners') },
        { path: '/admin/billing-export', icon: <Download size={18} />, label: t('nav.billingExport') },
        { path: '/admin/ledger', icon: <BookOpen size={18} />, label: t('nav.ledger') },
        { path: '/admin/purchase-invoices', icon: <FileInput size={18} />, label: t('nav.purchaseInvoices') },
        { path: '/admin/vat-return', icon: <Calculator size={18} />, label: t('nav.vatReturn') },
        { path: '/admin/financial-reports', icon: <FileBarChart size={18} />, label: t('nav.financialReports') },
      ]},
      { path: '/admin/consumers', icon: <Users size={20} />, label: t('nav.consumers') },
      { path: '/admin/meters', icon: <Gauge size={20} />, label: t('nav.meters') },
      { path: '/admin/zones', icon: <MapPin size={20} />, label: t('nav.zones') },
      { path: '/admin/users', icon: <Users size={20} />, label: t('nav.users') },
      { path: '/admin/pumps', icon: <Gauge size={20} />, label: t('nav.pumps') },
      { path: '/admin/announcements', icon: <Megaphone size={20} />, label: t('nav.announcements') },
      { path: '/admin/settings', icon: <Settings size={20} />, label: t('nav.settings') },
    ],
  };

  const items = navItems[user.role] || [];
  const roleLabels = { admin: t('role.admin'), kontrolor: t('role.kontrolor'), inkasant: t('role.inkasant'), vodoinstalater: t('role.vodoinstalater'), obracun: t('role.obracun'), kasa: t('role.kasa') };
  const roleColors = { admin: '#ef4444', kontrolor: '#8b5cf6', inkasant: '#3b82f6', vodoinstalater: '#0891b2', obracun: '#059669', kasa: '#d97706' };

  return (
    <>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => setMenuOpen(!menuOpen)} style={styles.menuBtn}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <img src="/logo.svg" alt={orgName} style={{ height: 28 }} />
          <span style={styles.title} data-header-title>{orgName}</span>
        </div>
        <div style={styles.headerRight}>
          {!online && <span style={styles.offlineBadge}>OFFLINE</span>}
          {pendingCount > 0 && (
            <button onClick={handleSync} style={styles.pendingBtn} disabled={syncing || !online}>
              <RefreshCw size={14} className={syncing ? 'spin' : ''} />
              {pendingCount}
            </button>
          )}
          <LanguageSwitcher />
          <span style={{...styles.roleBadge, background: roleColors[user.role]}}>
            {roleLabels[user.role]}
          </span>
        </div>
      </header>

      {menuOpen && <div style={styles.overlay} onClick={() => setMenuOpen(false)} />}
      
      <nav style={{...styles.nav, transform: menuOpen ? 'translateX(0)' : 'translateX(-100%)'}}>
        <div style={styles.navHeader}>
          <img src="/logo.svg" alt={orgName} style={{ height: 36 }} />
          <div>
            <div style={styles.navTitle}>{orgName}</div>
            <div style={styles.navSub}>{user.name}</div>
          </div>
        </div>

        <div style={styles.navScroll}>
          {items.map((item, idx) => {
            if (item.group) {
              const childActive = item.children.some(c => isActive(c.path));
              const isOpen = financeOpen || childActive;
              return (
                <React.Fragment key={item.label}>
                  <button onClick={() => setFinanceOpen(o => !o)}
                    style={{...styles.navItem, ...styles.navGroup, ...(childActive ? styles.navGroupActive : {})}}>
                    {item.icon}
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {isOpen ? <ChevronDown size={16} color="#94a3b8" /> : <ChevronRight size={16} color="#94a3b8" />}
                  </button>
                  {isOpen && item.children.map(child => (
                    <Link key={child.path} to={child.path} onClick={() => setMenuOpen(false)}
                      style={{...styles.navItem, ...styles.navSubItem, ...(isActive(child.path) ? styles.navItemActive : {})}}>
                      {child.icon}
                      <span style={{ flex: 1 }}>{child.label}</span>
                    </Link>
                  ))}
                </React.Fragment>
              );
            }
            return item.external ? (
              <a key={item.path} href={item.path} onClick={() => setMenuOpen(false)}
                style={{...styles.navItem, ...(location.pathname.startsWith(item.path) ? styles.navItemActive : {})}}>
                {item.icon}
                <span>{item.label}</span>
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8' }}>↗</span>
              </a>
            ) : (
              <Link key={item.path} to={item.path} onClick={() => setMenuOpen(false)}
                style={{...styles.navItem, ...(isActive(item.path) ? styles.navItemActive : {})}}>
                {item.icon}
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.badge > 0 && (
                  <span style={styles.navBadge}>{item.badge}</span>
                )}
              </Link>
            );
          })}
        </div>

        <div style={styles.navFooter}>
          <button onClick={() => { setPwModalOpen(true); setMenuOpen(false); }} style={styles.pwBtn}>
            <Key size={18} />
            <span>{t('nav.changePassword')}</span>
          </button>
          <button onClick={handleLogout} style={styles.logoutBtn}>
            <LogOut size={18} />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </nav>

      {pwModalOpen && <PasswordModal onClose={() => setPwModalOpen(false)} />}
    </>
  );
}

// ─── Password Change Modal ──────────────────────────────────────────────────
function PasswordModal({ onClose }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!current || !newPw || !confirm) {
      setError(t('password.allRequired'));
      return;
    }
    if (newPw !== confirm) {
      setError(t('password.mismatch'));
      return;
    }
    if (newPw === current) {
      setError(t('password.sameAsOld'));
      return;
    }
    setSaving(true);
    try {
      await api.put('/auth/password', { currentPassword: current, newPassword: newPw });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || t('password.error'));
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <>
        <div style={modalStyles.overlay} onClick={onClose} />
        <div style={modalStyles.modal}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircle size={48} color="#10b981" style={{ marginBottom: 12 }} />
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#334155', margin: '0 0 6px' }}>
              {t('password.successTitle')}
            </h3>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>
              {t('password.successDesc')}
            </p>
            <button onClick={onClose} style={modalStyles.primaryBtn}>{t('password.close')}</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={modalStyles.overlay} onClick={onClose} />
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <Key size={18} color="#0c4a6e" />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0c4a6e', margin: 0 }}>{t('password.title')}</h3>
          <button onClick={onClose} style={modalStyles.closeBtn}><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '16px 20px 20px' }}>
          {error && (
            <div style={modalStyles.error}>
              <AlertTriangle size={14} />
              {error}
            </div>
          )}

          {/* Current password */}
          <label style={modalStyles.label}>{t('password.current')}</label>
          <div style={modalStyles.inputWrap}>
            <input
              type={showCurrent ? 'text' : 'password'}
              value={current}
              onChange={e => setCurrent(e.target.value)}
              style={modalStyles.input}
              placeholder={t('password.currentPlaceholder')}
              autoComplete="current-password"
              autoFocus
            />
            <button type="button" onClick={() => setShowCurrent(s => !s)} style={modalStyles.eyeBtn}>
              {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>

          {/* New password */}
          <label style={modalStyles.label}>{t('password.new')}</label>
          <div style={modalStyles.inputWrap}>
            <input
              type={showNew ? 'text' : 'password'}
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              style={modalStyles.input}
              placeholder={t('password.newPlaceholder')}
              autoComplete="new-password"
            />
            <button type="button" onClick={() => setShowNew(s => !s)} style={modalStyles.eyeBtn}>
              {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>

          {/* Confirm */}
          <label style={modalStyles.label}>{t('password.confirm')}</label>
          <div style={modalStyles.inputWrap}>
            <input
              type={showNew ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              style={{
                ...modalStyles.input,
                borderColor: confirm && confirm !== newPw ? '#ef4444' : '#e2e8f0',
              }}
              placeholder={t('password.confirmPlaceholder')}
              autoComplete="new-password"
            />
            {confirm && confirm === newPw && (
              <CheckCircle size={15} color="#10b981" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }} />
            )}
          </div>

          {/* Buttons */}
          <div style={modalStyles.actions}>
            <button type="button" onClick={onClose} style={modalStyles.cancelBtn}>{t('password.cancel')}</button>
            <button type="submit" disabled={saving} style={modalStyles.primaryBtn}>
              {saving ? t('password.saving') : t('password.save')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

const styles = {
  header: {
    position: 'fixed', top: 0, left: 0, right: 0, height: 56,
    background: '#fff', borderBottom: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 12px', zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  menuBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#334155', display: 'flex' },
  title: { fontWeight: 700, fontSize: 17, color: '#0c4a6e' },
  roleBadge: {
    color: '#fff', padding: '2px 10px', borderRadius: 12,
    fontSize: 11, fontWeight: 600,
  },
  offlineBadge: {
    background: '#fef3c7', color: '#92400e', padding: '2px 8px',
    borderRadius: 4, fontSize: 10, fontWeight: 700,
  },
  pendingBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: '#fffbeb', color: '#92400e', padding: '3px 10px',
    borderRadius: 12, fontSize: 12, fontWeight: 600,
    border: '1px solid #fde68a', cursor: 'pointer',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200,
  },
  nav: {
    position: 'fixed', top: 0, left: 0, bottom: 0, width: 260,
    background: '#fff', zIndex: 300, transition: 'transform 0.2s ease',
    boxShadow: '4px 0 12px rgba(0,0,0,0.1)', display: 'flex',
    flexDirection: 'column',
  },
  navHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '20px 20px 16px', borderBottom: '1px solid #e2e8f0',
  },
  navTitle: { fontWeight: 700, fontSize: 18, color: '#0c4a6e' },
  navSub: { fontSize: 13, color: '#64748b' },
  navScroll: { flex: 1, overflowY: 'auto', padding: '8px 0' },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 20px', color: '#334155', textDecoration: 'none',
    fontSize: 14, fontWeight: 500,
  },
  navItemActive: {
    background: '#f0f9ff', color: '#0c4a6e', fontWeight: 600,
    borderRight: '3px solid #0ea5e9',
  },
  navGroup: {
    width: '100%', border: 'none', background: 'none',
    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
  },
  navGroupActive: {
    color: '#0c4a6e', fontWeight: 600,
  },
  navSubItem: {
    paddingLeft: 44, fontSize: 13, padding: '9px 20px 9px 48px',
  },
  navFooter: {
    borderTop: '1px solid #e2e8f0', padding: '8px 0',
  },
  pwBtn: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 20px', color: '#0c4a6e', background: 'none',
    border: 'none', width: '100%', textAlign: 'left',
    fontSize: 14, fontWeight: 500, cursor: 'pointer',
  },
  logoutBtn: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 20px', color: '#ef4444', background: 'none',
    border: 'none', width: '100%', textAlign: 'left',
    fontSize: 14, fontWeight: 500, cursor: 'pointer',
  },
  navBadge: {
    background: '#ef4444', color: '#fff', borderRadius: 10,
    padding: '1px 7px', fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'center',
  },
};

const modalStyles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    zIndex: 1000, backdropFilter: 'blur(2px)',
  },
  modal: {
    position: 'fixed', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#fff', borderRadius: 16, width: 'min(380px, 90vw)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)', zIndex: 1001,
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
  },
  closeBtn: {
    marginLeft: 'auto', background: 'none', border: 'none',
    cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 4,
  },
  label: {
    display: 'block', fontSize: 12, fontWeight: 600,
    color: '#475569', marginBottom: 4, marginTop: 14,
  },
  inputWrap: {
    position: 'relative',
  },
  input: {
    width: '100%', padding: '10px 36px 10px 12px', fontSize: 14,
    border: '1.5px solid #e2e8f0', borderRadius: 10, color: '#334155',
    outline: 'none', boxSizing: 'border-box',
  },
  eyeBtn: {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#94a3b8', display: 'flex', padding: 2,
  },
  error: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#fef2f2', color: '#dc2626', borderRadius: 8,
    padding: '8px 12px', fontSize: 13, fontWeight: 500,
  },
  actions: {
    display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20,
  },
  cancelBtn: {
    padding: '9px 18px', background: '#f1f5f9', color: '#64748b',
    border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '9px 22px', background: '#0c4a6e', color: '#fff',
    border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
    cursor: 'pointer',
  },
};
